/**
 * Observações da receita de óculos — chips, ordem e "padrões".
 *
 * Saiu do `LensEditor` pra que o ditado (spec 009) monte a MESMA linha de
 * observações que o modal monta: os termos marcados como padrão (estrela)
 * mais o que o médico ditou, casado com os chips existentes quando der.
 * Persistido por navegador, como sempre foi.
 */

const OBS_STORAGE_KEY_V1 = 'dratento-lens-obs-templates';
const OBS_STORAGE_KEY_V2 = 'dratento-lens-obs-templates-v2';
const OBS_DEFAULTS_KEY = 'dratento-lens-obs-defaults';

export const DEFAULT_OBS_TEMPLATES = [
  'Uso constante',
  'Uso para longe',
  'Uso para perto',
  'Lentes progressivas',
  'Lentes bifocais',
  'Antirreflexo',
  'Fotossensível / Transitions',
  'Filtro de luz azul',
  'Lentes de policarbonato',
  'Lentes de resina',
  'Uso para computador',
  'Armação esportiva',
  'Proteção UV 400',
];

/** Separador entre observações — o mesmo que os chips usam. */
export const OBS_SEP = '; ';

function ls(): Storage | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch {
    return null;
  }
}

/**
 * Carrega a ordem persistida. Se houver registro v2, usa direto.
 * Senão, faz a migração automática do v1 (defaults + custom legado)
 * pra que o médico não perca os termos personalizados que já tinha.
 */
export function loadOrderedObsTemplates(): string[] {
  const store = ls();
  try {
    const v2 = store?.getItem(OBS_STORAGE_KEY_V2);
    if (v2) {
      const parsed = JSON.parse(v2) as string[];
      if (Array.isArray(parsed) && parsed.every((s) => typeof s === 'string')) {
        return parsed;
      }
    }
  } catch { /* ignore */ }
  // Migração v1 → v2: defaults + custom antigos no fim, na mesma ordem.
  try {
    const legacy = store?.getItem(OBS_STORAGE_KEY_V1);
    const legacyCustom: string[] = legacy ? JSON.parse(legacy) : [];
    return [...DEFAULT_OBS_TEMPLATES, ...legacyCustom];
  } catch {
    return [...DEFAULT_OBS_TEMPLATES];
  }
}

export function saveOrderedObsTemplates(list: string[]) {
  ls()?.setItem(OBS_STORAGE_KEY_V2, JSON.stringify(list));
}

/** Termos marcados como "padrão" — aplicados automaticamente quando o
 *  campo de observações abre vazio (e em toda receita ditada). */
export function loadDefaultObs(): string[] {
  try {
    const raw = ls()?.getItem(OBS_DEFAULTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed) && parsed.every((s) => typeof s === 'string')) {
      return parsed;
    }
  } catch { /* ignore */ }
  return [];
}

export function saveDefaultObs(list: string[]) {
  ls()?.setItem(OBS_DEFAULTS_KEY, JSON.stringify(list));
}

// ── Composição pro ditado ────────────────────────────────────────────────

function norm(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Palavras de ligação que o ditado traz e o chip não tem. */
const IGNORAR = new Set(['com', 'e', 'de', 'para', 'lente', 'lentes', 'protecao', 'uso']);

function palavras(s: string): string[] {
  return norm(s).split(' ').filter((w) => w && !IGNORAR.has(w));
}

/**
 * Casa um trecho ditado com um chip: todas as palavras significativas do
 * chip aparecem no trecho (ou vice-versa, quando o trecho é curto).
 * "filtro de luz azul" ↔ "proteção de luz azul" casa por "luz azul";
 * "transitions" ↔ "Fotossensível / Transitions" casa por "transitions".
 */
function casaChip(trecho: string, chip: string): boolean {
  const t = palavras(trecho);
  const c = palavras(chip);
  if (t.length === 0 || c.length === 0) return false;
  const contem = (a: string[], b: string[]) => b.every((w) => a.some((x) => x.startsWith(w.slice(0, Math.max(4, w.length - 1)))));
  return contem(t, c) || (t.length <= 2 && contem(c, t));
}

/** Quebra o texto ditado em trechos: ";", ",", " e ", " com ". */
export function splitObservacoes(texto: string): string[] {
  return texto
    .split(/[;,.]+|\s+\be\b\s+|\s+\bcom\b\s+/i)
    .map((s) => s.trim())
    .filter(Boolean);
}

export interface ComposeObsOptions {
  /** Termos padrão (estrela). Default: os persistidos no navegador. */
  defaults?: string[];
  /** Chips conhecidos, na ordem da tela. Default: os persistidos. */
  templates?: string[];
}

/**
 * Observações da receita ditada = padrões + o que foi dito, sem repetir e
 * na ordem dos chips (o que não casou com chip nenhum vai pro fim, como o
 * médico falou). Pedido do Leopoldo, 17/09/2026: "lentes progressivas"
 * ditado tem que sair junto com antirreflexo / transitions / luz azul.
 */
export function composeLensObservations(dictated: string, opts: ComposeObsOptions = {}): string {
  const defaults = opts.defaults ?? loadDefaultObs();
  const templates = opts.templates ?? loadOrderedObsTemplates();
  const escolhidos = new Set<string>(defaults);
  const soltos: string[] = [];
  for (const trecho of splitObservacoes(dictated)) {
    const chip = templates.find((c) => casaChip(trecho, c));
    if (chip) escolhidos.add(chip);
    else if (!soltos.some((s) => norm(s) === norm(trecho))) soltos.push(trecho);
  }
  const ordenados = templates.filter((c) => escolhidos.has(c));
  // Padrão que não está mais na lista de chips ainda vale.
  const forinha = defaults.filter((d) => !templates.includes(d));
  return [...ordenados, ...forinha, ...soltos].join(OBS_SEP);
}

/** União de duas linhas de observações, sem repetir (pra fundir ditados). */
export function mergeObservacoes(a: string, b: string): string {
  const out: string[] = [];
  for (const s of [...a.split(OBS_SEP), ...b.split(OBS_SEP)]) {
    const t = s.trim();
    if (t && !out.some((o) => norm(o) === norm(t))) out.push(t);
  }
  return out.join(OBS_SEP);
}
